import {useCallback, useMemo, useState, useEffect} from "react";
import {toast} from "sonner";
import Shell from "../../components/shell";
import DataTable from "../../components/tables";
import {
  useCreateMembershipType,
  useDeleteMembershipType,
  useMembershipTypes,
  useUpdateMembershipType,
} from "../../hooks/use-membership";

export default function MembershipTypesPage() {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const {data, isLoading} = useMembershipTypes({search, limit: 20});
  const createMut = useCreateMembershipType();
  const updateMut = useUpdateMembershipType();
  const deleteMut = useDeleteMembershipType();

  const items = data?.data?.documents || [];

  const onSave = useCallback(
    async (form) => {
      try {
        if (editing?._id) {
          await updateMut.mutateAsync({id: editing._id, payload: form});
          toast.success("Updated");
        } else {
          await createMut.mutateAsync(form);
          toast.success("Created");
        }
        setOpen(false);
        setEditing(null);
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed");
      }
    },
    [editing, updateMut, createMut]
  );

  const onDelete = useCallback(
    async (id) => {
      if (!window.confirm("Delete this membership type?")) return;
      try {
        await deleteMut.mutateAsync(id);
        toast.success("Deleted");
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed");
      }
    },
    [deleteMut]
  );

  const columns = useMemo(
    () => [
      {
        header: "TITLE",
        accessorKey: "title",
        cell: ({getValue}) => <span className="uppercase">{getValue()}</span>,
      },
      {
        header: "CODE",
        accessorKey: "code",
        cell: ({getValue}) => <span className="uppercase">{getValue()}</span>,
      },
      {
        header: "SLUG",
        accessorKey: "slug",
        cell: ({getValue}) => <span className="uppercase">{getValue()}</span>,
      },
      {
        header: "AMOUNT",
        accessorKey: "amount",
        cell: ({getValue}) => (
          <span>₦{Number(getValue() || 0).toLocaleString("en-NG")}</span>
        ),
      },
      {
        header: "STATUS",
        accessorKey: "isActive",
        cell: ({getValue}) => {
          const isActive = getValue();
          return (
            <span
              className={`uppercase px-2 py-1 rounded ${
                isActive
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {isActive ? "ACTIVE" : "INACTIVE"}
            </span>
          );
        },
      },
      {
        header: "ACTION",
        accessorKey: "action",
        cell: ({row}) => (
          <div className="flex items-center gap-2">
            <button
              className="rounded border px-2 py-1 hover:bg-gray-50"
              onClick={() => {
                setEditing(row.original);
                setOpen(true);
              }}
            >
              Edit
            </button>
            <button
              className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700"
              onClick={() => onDelete(row.original._id)}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [onDelete]
  );

  const headerBtnAction = {
    isActive: true,
    title: "+ New",
    onClick: () => {
      setEditing(null);
      setOpen(true);
    },
  };

  return (
    <div>
      <Shell
        pageHeader={"Membership Types"}
        pageTitle={"Membership Types"}
        btnAction={headerBtnAction}
      >
        <div>
          <div>
            <DataTable
              data={items}
              columns={columns}
              globalFilter={search}
              setGlobalFilter={setSearch}
              pageSize={10}
              isLoading={isLoading}
            />
          </div>

          {open && (
            <EditModal
              defaults={editing}
              onClose={() => setOpen(false)}
              onSave={onSave}
              isSaving={createMut.isPending || updateMut.isPending}
            />
          )}
        </div>
      </Shell>
    </div>
  );
}

function EditModal({defaults, onClose, onSave, isSaving}) {
  const [form, setForm] = useState({
    title: defaults?.title || "",
    code: defaults?.code || "",
    slug: defaults?.slug || "",
    amount: defaults?.amount ?? 0,
    currency: defaults?.currency || "NGN",
    description: defaults?.description || "",
    maxDurationDays: defaults?.maxDurationDays ?? 0,
    isActive: defaults?.isActive ?? true,
    sortOrder: defaults?.sortOrder ?? 0,
  });

  const slugify = (s) =>
    s
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  useEffect(() => {
    const value = `${form.title} ${form.code}`.trim();
    const next = slugify(value);
    if (form.slug !== next) {
      setForm((s) => ({...s, slug: next}));
    }
  }, [form.title, form.code, form.slug]);

  const onChange = (k, v) => setForm((s) => ({...s, [k]: v}));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 top-0 p-4">
      <div className="w-full max-w-xl rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {defaults?._id ? "Edit Membership Type" : "New Membership Type"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            ✕
          </button>
        </div>

        <div className="grid gap-3">
          <Field label="Title">
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              value={form.title}
              onChange={(e) => onChange("title", e.target.value)}
            />
          </Field>

          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Code">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={form.code}
                onChange={(e) => onChange("code", e.target.value)}
              />
            </Field>
            <Field label="Slug">
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={form.slug}
                readOnly
              />
            </Field>
            <Field label="Amount (NGN)">
              <input
                type="number"
                className="w-full rounded border px-3 py-2 text-sm"
                value={form.amount}
                onChange={(e) => onChange("amount", Number(e.target.value))}
              />
            </Field>
          </div>

          <Field label="Description">
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) => onChange("description", e.target.value)}
            />
          </Field>

          <div className="grid gap-3 md:grid-cols-3">
            <div className="col-span-full">
              <Field label="Currency">
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={form.currency}
                  onChange={(e) => onChange("currency", e.target.value)}
                />
              </Field>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => onChange("isActive", e.target.checked)}
            />
            Active
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            className="rounded border px-4 py-2 text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded bg-black px-4 py-2 text-sm text-white hover:bg-gray-900 disabled:opacity-60"
            onClick={() => onSave(form)}
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({label, children}) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-gray-600">{label}</div>
      {children}
    </div>
  );
}
